(() => {
const INCH_TO_CM = 2.54;
const FOOT_TO_METER = 0.3048;

const TARGETS = {
  dining: [
    { id: 'table', label: 'Dining table' },
    { id: 'open-floor', label: 'Main ceiling light' },
    { id: 'sideboard', label: 'Sideboard or buffet' }
  ],
  kitchen: [
    { id: 'island', label: 'Island or peninsula' },
    { id: 'open-floor', label: 'Main ceiling light' },
    { id: 'task-zone', label: 'Sink or work area' }
  ],
  living: [
    { id: 'open-floor', label: 'Main ceiling light' },
    { id: 'reading-area', label: 'Seating or reading area' },
    { id: 'accent-wall', label: 'Accent wall' }
  ],
  bedroom: [
    { id: 'open-floor', label: 'Main ceiling light' },
    { id: 'bedside', label: 'Bedside' },
    { id: 'dresser', label: 'Dresser or reading area' }
  ],
  bathroom: [
    { id: 'vanity', label: 'Bathroom mirror or vanity' },
    { id: 'open-floor', label: 'Main ceiling light' },
    { id: 'wet-zone', label: 'Shower area' }
  ],
  entryway: [
    { id: 'single-height', label: 'Standard-height entryway' },
    { id: 'double-height', label: 'Two-story foyer' }
  ],
  hallway: [
    { id: 'ceiling-path', label: 'Ceiling lights along the hallway' },
    { id: 'sconce-path', label: 'Wall sconces along the hallway' }
  ],
  office: [
    { id: 'open-floor', label: 'Main ceiling light' },
    { id: 'desk', label: 'Desk or task area' }
  ]
};

const ROOM_FIELD_CONFIG = {
  fields: ['length', 'width', 'ceilingHeight'],
  labels: { length: 'Room length', width: 'Room width', ceilingHeight: 'Ceiling height' },
  kinds: { length: 'room', width: 'room', ceilingHeight: 'height' },
  defaults: { length: 12, width: 14, ceilingHeight: 9 }
};

const PLANNER_CONFIG = {
  'open-floor': ROOM_FIELD_CONFIG,
  'single-height': ROOM_FIELD_CONFIG,
  'double-height': ROOM_FIELD_CONFIG,
  'ceiling-path': ROOM_FIELD_CONFIG,
  table: {
    fields: ['length', 'width'],
    labels: { length: 'Table length', width: 'Table width' },
    kinds: { length: 'surface', width: 'surface' },
    defaults: { length: 84, width: 40, tableShape: 'rectangular' }
  },
  island: {
    fields: ['length', 'width', 'ceilingHeight'],
    labels: { length: 'Island length', width: 'Island width', ceilingHeight: 'Ceiling height' },
    kinds: { length: 'surface', width: 'surface', ceilingHeight: 'height' },
    defaults: { length: 72, width: 36, ceilingHeight: 9 }
  },
  vanity: {
    fields: ['length', 'width'],
    labels: { length: 'Mirror width', width: 'Mirror height' },
    kinds: { length: 'surface', width: 'surface' },
    defaults: { length: 36, width: 30 }
  },
  bedside: {
    fields: ['width', 'surfaceHeight'],
    labels: { width: 'Nightstand width', surfaceHeight: 'Nightstand height' },
    kinds: { width: 'surface', surfaceHeight: 'surface' },
    defaults: { width: 20, surfaceHeight: 26 }
  },
  'sconce-path': {
    fields: ['length'],
    labels: { length: 'Hallway length' },
    kinds: { length: 'room' },
    defaults: { length: 24 }
  },
  sideboard: { fields: [], labels: {}, kinds: {}, defaults: {} },
  'task-zone': { fields: [], labels: {}, kinds: {}, defaults: {} },
  'reading-area': { fields: [], labels: {}, kinds: {}, defaults: {} },
  'accent-wall': { fields: [], labels: {}, kinds: {}, defaults: {} },
  dresser: { fields: [], labels: {}, kinds: {}, defaults: {} },
  'wet-zone': { fields: [], labels: {}, kinds: {}, defaults: {} },
  desk: { fields: [], labels: {}, kinds: {}, defaults: {} }
};

const ROUND_TABLE_CONFIG = {
  fields: ['length'],
  labels: { length: 'Round-table diameter or square-table width' },
  kinds: { length: 'surface' },
  defaults: { length: 48, tableShape: 'round' }
};

const ROOM_DEFAULTS = {
  'dining:table': { length: 84, width: 40, tableShape: 'rectangular' },
  'dining:open-floor': { length: 12, width: 14, ceilingHeight: 9 },
  'kitchen:island': { length: 72, width: 36, ceilingHeight: 9 },
  'kitchen:open-floor': { length: 12, width: 14, ceilingHeight: 9 },
  'living:open-floor': { length: 12, width: 14, ceilingHeight: 9 },
  'bedroom:open-floor': { length: 12, width: 12, ceilingHeight: 9 },
  'bedroom:bedside': { width: 20, surfaceHeight: 26 },
  'bathroom:vanity': { length: 36, width: 30 },
  'bathroom:open-floor': { length: 8, width: 10, ceilingHeight: 8 },
  'entryway:single-height': { length: 10, width: 8, ceilingHeight: 9 },
  'entryway:double-height': { length: 12, width: 10, ceilingHeight: 18 },
  'hallway:ceiling-path': { length: 16, width: 4, ceilingHeight: 8 },
  'hallway:sconce-path': { length: 24 },
  'office:open-floor': { length: 10, width: 12, ceilingHeight: 9 }
};

const COLLECTIONS = {
  pendant: 'https://www.mergelighting.com/collections/pendant-lamp',
  chandelier: 'https://www.mergelighting.com/collections/chandelier',
  ceiling: 'https://www.mergelighting.com/collections/ceiling-lamp',
  wall: 'https://www.mergelighting.com/collections/wall-lamp',
  table: 'https://www.mergelighting.com/collections/table-lamp',
  floor: 'https://www.mergelighting.com/collections/floor-lamps'
};

const BASE_DISCLAIMER = 'Use these ranges as starting points. Always follow the product dimensions, installation instructions, safety and location ratings, weight requirements, and local electrical codes. Confirm clearances and sightlines before installation.';

const roomGuideData = {
  dining: {
    label: 'Dining room',
    context: 'Dining table',
    title: 'Center the fixture over the dining table',
    summary: 'Use the table—not the center of the room—as your reference, especially when the table is off-center.',
    facts: [
      ['Start with', 'A pendant or chandelier'],
      ['Place it', 'Center over the table, not the room'],
      ['Add next', 'Wall sconces or table lamps for softer light']
    ],
    links: [
      { label: 'Shop chandeliers', url: COLLECTIONS.chandelier },
      { label: 'Shop pendant lights', url: COLLECTIONS.pendant }
    ]
  },
  kitchen: {
    label: 'Kitchen',
    context: 'Kitchen island',
    title: 'Light the island work surface',
    summary: 'Use the island as your reference and keep the fixture group within its edges so it provides effective task lighting without blocking sightlines.',
    facts: [
      ['Start with', 'Pendants or a linear chandelier'],
      ['Place it', 'Center over the island work surface'],
      ['Add next', 'Under-cabinet lighting for detailed tasks']
    ],
    links: [
      { label: 'Shop pendant lights', url: COLLECTIONS.pendant },
      { label: 'Shop chandeliers', url: COLLECTIONS.chandelier }
    ]
  },
  living: {
    label: 'Living room',
    context: 'Seating area',
    title: 'Build light around the seating area',
    summary: 'Treat the sofa and chairs as one lighting area, then add lamps at reading height instead of relying only on the ceiling light.',
    facts: [
      ['Start with', 'Ceiling light or chandelier'],
      ['Place it', 'Align with the seating group or coffee table'],
      ['Add next', 'Floor and table lamps beside seats']
    ],
    links: [
      { label: 'Shop floor lamps', url: COLLECTIONS.floor },
      { label: 'Shop table lamps', url: COLLECTIONS.table }
    ]
  },
  bedroom: {
    label: 'Bedroom',
    context: 'Bed and nightstand',
    title: 'Keep bedside lighting within easy reach',
    summary: 'Start with soft general lighting, then position the bedside light for reading and easy switching while keeping the bulb out of view from the bed.',
    facts: [
      ['Start with', 'Soft ceiling light'],
      ['Place it', 'Beside the bed within easy reach'],
      ['Add next', 'Table lamps or bedside pendants']
    ],
    links: [
      { label: 'Shop table lamps', url: COLLECTIONS.table },
      { label: 'Shop pendant lights', url: COLLECTIONS.pendant }
    ]
  },
  bathroom: {
    label: 'Bathroom',
    context: 'Vanity mirror',
    title: 'Place lights beside the mirror',
    summary: 'Lighting the face from both sides creates more even illumination than a single strong light above the mirror. Confirm that each fixture is listed for its exact bathroom location.',
    facts: [
      ['Start with', 'Paired vanity sconces'],
      ['Place it', 'On both sides of the mirror when possible'],
      ['Confirm', 'The correct damp- or wet-location rating']
    ],
    links: [
      { label: 'Shop wall lights', url: COLLECTIONS.wall },
      { label: 'Shop ceiling lights', url: COLLECTIONS.ceiling }
    ]
  },
  entryway: {
    label: 'Entryway',
    context: 'Front door and console',
    title: 'Keep the path from the front door clear',
    summary: 'Choose an entry light that clears the door swing, walking path, and any console table below it.',
    facts: [
      ['Start with', 'Pendant, chandelier, or ceiling light'],
      ['Place it', 'Center on the entry path or foyer table'],
      ['Confirm', 'Door swing, headroom, and fixture support']
    ],
    links: [
      { label: 'Shop chandeliers', url: COLLECTIONS.chandelier },
      { label: 'Shop ceiling lights', url: COLLECTIONS.ceiling }
    ]
  },
  hallway: {
    label: 'Hallway',
    context: 'Hallway path',
    title: 'Create an even rhythm of light along the hallway',
    summary: 'Several smaller fixtures spaced along a long hallway usually feel more even and comfortable than one bright light in the center.',
    facts: [
      ['Start with', 'Repeated ceiling lights or sconces'],
      ['Place it', 'Keep an even visual rhythm down the hall'],
      ['Confirm', 'Headroom and how far wall fixtures extend into the hallway']
    ],
    links: [
      { label: 'Shop ceiling lights', url: COLLECTIONS.ceiling },
      { label: 'Shop wall lights', url: COLLECTIONS.wall }
    ]
  },
  office: {
    label: 'Home office',
    context: 'Desk and work surface',
    title: 'Separate task light from screen light',
    summary: 'Use an adjustable lamp for paperwork and detailed tasks, then keep the surrounding light soft enough to reduce screen reflections.',
    facts: [
      ['Start with', 'Adjustable desk or task lamp'],
      ['Place it', 'On the side opposite your writing hand and away from screen glare'],
      ['Add next', 'Diffuse ambient or ceiling light']
    ],
    links: [
      { label: 'Shop table lamps', url: COLLECTIONS.table },
      { label: 'Shop floor lamps', url: COLLECTIONS.floor }
    ]
  }
};

function convertMeasurement(value, kind, fromUnit, toUnit) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || fromUnit === toUnit) return numericValue;

  if (kind === 'surface') {
    return fromUnit === 'us'
      ? Number((numericValue * INCH_TO_CM).toFixed(1))
      : Number((numericValue / INCH_TO_CM).toFixed(1));
  }

  return fromUnit === 'us'
    ? Number((numericValue * FOOT_TO_METER).toFixed(2))
    : Number((numericValue / FOOT_TO_METER).toFixed(1));
}

function getTargetsForRoom(room) {
  return TARGETS[room] ? TARGETS[room].map((target) => ({ ...target })) : [];
}

function getPlannerConfig(room, target, tableShape = 'rectangular') {
  const targetExists = TARGETS[room]?.some((candidate) => candidate.id === target);
  const config = targetExists
    ? (target === 'table' && tableShape === 'round' ? ROUND_TABLE_CONFIG : PLANNER_CONFIG[target])
    : null;
  if (!config) throw new RangeError('Choose what you are lighting.');
  return {
    fields: [...config.fields],
    labels: { ...config.labels },
    kinds: { ...config.kinds },
    defaults: { ...config.defaults }
  };
}

function getDefaultPlannerInput(room, target, unit = 'us', tableShape = 'rectangular') {
  const normalizedUnit = unit === 'metric' ? 'metric' : 'us';
  const normalizedTableShape = tableShape === 'round' ? 'round' : 'rectangular';
  const config = getPlannerConfig(room, target, normalizedTableShape);
  const roomDefaults = target === 'table' && normalizedTableShape === 'round'
    ? {}
    : (ROOM_DEFAULTS[`${room}:${target}`] || {});
  const source = { ...config.defaults, ...roomDefaults };
  const result = { room, target, unit: normalizedUnit };

  for (const field of config.fields) {
    const value = source[field];
    result[field] = normalizedUnit === 'metric'
      ? convertMeasurement(value, config.kinds[field], 'us', 'metric')
      : value;
  }

  if (target === 'table') result.tableShape = normalizedTableShape;
  return result;
}

function measurementLimits(kind, unit) {
  if (kind === 'surface') return unit === 'us' ? { min: 1, max: 600 } : { min: 2.5, max: 1524 };
  if (kind === 'height') return unit === 'us' ? { min: 7, max: 40 } : { min: 2.13, max: 12.19 };
  return unit === 'us' ? { min: 3, max: 100 } : { min: 0.91, max: 30.48 };
}

function articleFor(label) {
  return /^[aeiou]/i.test(label) ? 'an' : 'a';
}

function validatePlannerInput(input) {
  const errors = {};
  const unit = input.unit === 'metric' ? 'metric' : 'us';
  const targets = getTargetsForRoom(input.room);

  if (!TARGETS[input.room]) {
    errors.room = 'Choose a room.';
    return { valid: false, errors };
  }
  if (!targets.some((target) => target.id === input.target)) {
    errors.target = 'Choose what you are lighting.';
    return { valid: false, errors };
  }

  const config = getPlannerConfig(input.room, input.target, input.tableShape);
  for (const field of config.fields) {
    const value = Number(input[field]);
    const label = config.labels[field].toLowerCase();
    const limits = measurementLimits(config.kinds[field], unit);

    if (!Number.isFinite(value) || value <= 0) {
      errors[field] = `Enter ${articleFor(label)} ${label} greater than 0.`;
    } else if (value > limits.max) {
      errors[field] = `Enter ${articleFor(label)} ${label} less than or equal to ${limits.max}.`;
    } else if (value < limits.min) {
      errors[field] = `Enter ${articleFor(label)} ${label} of at least ${limits.min}.`;
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

function toInches(value, kind, unit) {
  if (unit === 'us') return kind === 'surface' ? Number(value) : Number(value) * 12;
  return kind === 'surface' ? Number(value) / INCH_TO_CM : Number(value) / FOOT_TO_METER * 12;
}

function normalizedInput(input) {
  const unit = input.unit === 'metric' ? 'metric' : 'us';
  const config = getPlannerConfig(input.room, input.target, input.tableShape);
  const result = { ...input, unit, tableShape: input.tableShape === 'round' ? 'round' : 'rectangular' };

  for (const field of config.fields) {
    result[`${field}Inches`] = toInches(input[field], config.kinds[field], unit);
  }
  return result;
}

function whole(value) {
  return Math.round(value);
}

function range(min, max, unit, suffix = '') {
  if (unit === 'metric') return `${whole(min * INCH_TO_CM)}-${whole(max * INCH_TO_CM)} cm${suffix}`;
  return `${whole(min)}-${whole(max)} in${suffix}`;
}

function single(value, unit, suffix = '') {
  if (unit === 'metric') return `${whole(value * INCH_TO_CM)} cm${suffix}`;
  return `${whole(value)} in${suffix}`;
}

function link(label, url) {
  return { label, url };
}

function addLinks(result, collectionLinks) {
  return { ...result, collectionLinks, collectionUrl: collectionLinks[0].url };
}

function roomColor(room) {
  return ['kitchen', 'bathroom', 'office'].includes(room)
    ? '3000-3500K · Warm to neutral white'
    : '2700-3000K · Warm white';
}

function displayRow(id, label, value) {
  return { id, label, value };
}

function displayGroup(id, label, items) {
  return { id, label, items };
}

const STOCK_DIAMETER_TIERS = [
  { inches: 12, centimeters: 30 },
  { inches: 16, centimeters: 40 },
  { inches: 20, centimeters: 50 },
  { inches: 24, centimeters: 60 },
  { inches: 30, centimeters: 75 },
  { inches: 32, centimeters: 80 },
  { inches: 36, centimeters: 90 },
  { inches: 40, centimeters: 100 },
  { inches: 48, centimeters: 120 },
  { inches: 60, centimeters: 150 }
];

function tierLabel(tier) {
  return `${tier.inches} in (${tier.centimeters} cm)`;
}

function stockAwareDiameterRange(professionalDiameter) {
  let statementIndex = STOCK_DIAMETER_TIERS.findIndex((tier) => tier.inches >= professionalDiameter);
  if (statementIndex === -1) statementIndex = STOCK_DIAMETER_TIERS.length - 1;

  const statement = STOCK_DIAMETER_TIERS[statementIndex];
  const recommendedHigh = STOCK_DIAMETER_TIERS[Math.max(0, statementIndex - 1)];
  const recommendedLow = STOCK_DIAMETER_TIERS[Math.max(0, statementIndex - 2)];
  const compact = STOCK_DIAMETER_TIERS[Math.max(0, statementIndex - 3)];
  return { statement, recommendedHigh, recommendedLow, compact };
}

function stockAwareSizeTiers(professionalDiameter) {
  const { statement, recommendedHigh, recommendedLow, compact } = stockAwareDiameterRange(professionalDiameter);
  const recommended = recommendedLow.inches === recommendedHigh.inches
    ? `About ${tierLabel(recommendedHigh)}`
    : `${recommendedLow.inches}-${recommendedHigh.inches} in (${recommendedLow.centimeters}-${recommendedHigh.centimeters} cm)`;

  return {
    recommended,
    compact: `About ${tierLabel(compact)}`,
    statement: `About ${tierLabel(statement)}`
  };
}

function tierSummary(sizeTiers) {
  return `Recommended: ${sizeTiers.recommended}; compact: ${sizeTiers.compact}; statement: ${sizeTiers.statement}`;
}

function openFloorRecommendation(input) {
  const lengthFeet = input.lengthInches / 12;
  const widthFeet = input.widthInches / 12;
  const rawDiameter = whole(lengthFeet + widthFeet);
  const narrowDimensionCap = Math.max(8, Math.floor(Math.min(input.lengthInches, input.widthInches) * 0.7));
  const diameter = Math.min(rawDiameter, narrowDimensionCap);
  const availableDrop = Math.max(0, input.ceilingHeightInches - 84);
  const exactSevenFoot = input.ceilingHeightInches === 84;
  const needsLightingPlan = Math.max(input.lengthInches, input.widthInches) > 420 || rawDiameter > 72;

  let fixtureType;
  let fixtureTypes;
  let collectionLinks;
  if (exactSevenFoot) {
    fixtureType = 'Ultra-low-profile or recessed lighting';
    fixtureTypes = ['ceiling', 'recessed'];
    collectionLinks = [link('Shop ceiling lights', COLLECTIONS.ceiling)];
  } else if (availableDrop <= 8) {
    fixtureType = 'Flush-mount ceiling light';
    fixtureTypes = ['ceiling'];
    collectionLinks = [link('Shop ceiling lights', COLLECTIONS.ceiling)];
  } else if (availableDrop <= 15) {
    fixtureType = 'Flush or compact semi-flush ceiling light';
    fixtureTypes = ['ceiling'];
    collectionLinks = [link('Shop ceiling lights', COLLECTIONS.ceiling)];
  } else {
    fixtureType = needsLightingPlan ? 'Multiple fixtures or a custom lighting plan' : 'Compact chandelier or ceiling fixture';
    fixtureTypes = needsLightingPlan ? ['chandelier', 'pendant', 'ceiling'] : ['chandelier', 'ceiling'];
    collectionLinks = [
      link('Shop chandeliers', COLLECTIONS.chandelier),
      link('Shop ceiling lights', COLLECTIONS.ceiling)
    ];
  }

  const capped = diameter < rawDiameter;
  const sizeTiers = stockAwareSizeTiers(diameter);
  const recommendedDiameter = stockAwareDiameterRange(diameter);
  const fixtureSize = needsLightingPlan
    ? `Use multiple fixtures; keep each fixture within about ${single(diameter, input.unit)} diameter`
    : `About ${single(diameter, input.unit)} diameter`;
  const hangingHeight = exactSevenFoot
    ? 'A surface-mounted fixture cannot provide a full 7 ft (84 in) of headroom'
    : `Maximum hanging length: ${single(availableDrop, input.unit)} from the ceiling to the bottom of the fixture (including canopy and suspension)`;
  const explanation = exactSevenFoot
    ? 'A 7-foot ceiling cannot provide a full 7 ft (84 in) of headroom once a surface-mounted fixture extends below the ceiling. Choose an ultra-low-profile or recessed light and confirm the clearance on site.'
    : `Keep at least ${input.unit === 'metric' ? '213 cm' : '7 ft (84 in)'} between the floor and the bottom of the fixture. The total hanging length includes the canopy, rod or chain, and fixture body.${capped ? ' The recommended diameter was reduced to fit the room\'s narrower dimension.' : ''}${needsLightingPlan ? ' A long or oversized room needs multiple light sources instead of one oversized fixture.' : ''}`;

  const mainScene = input.target === 'ceiling-path'
    ? 'hallway-ceiling'
    : ['single-height', 'double-height'].includes(input.target)
      ? 'entryway'
      : 'main-ceiling';
  const hallwayLengthFeet = input.lengthInches / 12;
  const hallwayMinimum = Math.max(1, Math.ceil(hallwayLengthFeet / 8));
  const hallwayMaximum = Math.max(hallwayMinimum, Math.ceil(hallwayLengthFeet / 6));
  const hallwayQuantity = hallwayMinimum === hallwayMaximum
    ? `${hallwayMinimum} ceiling fixtures as a starting point`
    : `${hallwayMinimum}-${hallwayMaximum} ceiling fixtures as a starting point`;
  const quantity = input.target === 'ceiling-path'
    ? hallwayQuantity
    : ['single-height', 'double-height'].includes(input.target)
      ? '1 entry fixture'
      : needsLightingPlan
        ? 'Multiple fixtures; confirm with a lighting plan'
        : '1 main fixture';
  const displayRows = input.target === 'ceiling-path'
    ? [
        displayRow('quantity', 'Quantity', quantity),
        displayRow('type', 'Fixture type', fixtureType),
        displayRow('clearance', 'Clearance', hangingHeight),
        displayRow('placement', 'Placement', 'Create an even path of light while keeping doors and openings clear')
      ]
    : ['single-height', 'double-height'].includes(input.target)
      ? [
          displayRow('quantity', 'Quantity', quantity),
          displayRow('type', 'Fixture type', fixtureType),
          displayRow('drop', 'Hanging length', hangingHeight),
          displayRow('placement', 'Placement', 'Keep the fixture clear of the front door, landing, and stairs')
        ]
      : [
          displayRow('quantity', 'Quantity', quantity),
          displayGroup('size', 'Size options', [
            { label: 'Recommended', value: sizeTiers.recommended },
            { label: 'Compact', value: sizeTiers.compact },
            { label: 'Statement', value: sizeTiers.statement }
          ]),
          displayRow('drop', 'Hanging length', hangingHeight),
          displayRow('placement', 'Placement', 'Center over the main area you want to light')
        ];

  return addLinks({
    fixtureType,
    fixtureSize,
    sizeTiers,
    tracking: {
      fixtureTypes,
      sizeKind: ['ceiling-path', 'single-height', 'double-height'].includes(input.target) ? 'none' : 'diameter',
      sizeMinInches: recommendedDiameter.recommendedLow.inches,
      sizeMaxInches: recommendedDiameter.recommendedHigh.inches,
      quantityMin: input.target === 'ceiling-path' ? hallwayMinimum : needsLightingPlan ? 2 : 1,
      quantityMax: input.target === 'ceiling-path' ? hallwayMaximum : needsLightingPlan ? null : 1
    },
    hangingHeight,
    maximumDropInches: availableDrop,
    spacing: 'Center over the main area you want to light, not automatically in the center of the ceiling',
    colorTemperature: roomColor(input.room),
    explanation,
    displayRows,
    diagram: {
      mode: 'room',
      primary: input.unit === 'metric' ? `${whole(diameter * INCH_TO_CM)} cm diameter` : `${diameter} in diameter`,
      secondary: exactSevenFoot ? 'On-site clearance check required' : `${single(availableDrop, input.unit)} maximum hanging length`,
      scene: mainScene,
      variant: input.target,
      measurements: {
        width: sizeTiers.recommended,
        drop: exactSevenFoot ? 'On-site clearance check required' : `${single(availableDrop, input.unit)} maximum hanging length`,
        clearance: exactSevenFoot ? '' : (input.unit === 'metric' ? 'At least 213 cm from floor to fixture bottom' : 'At least 7 ft (84 in) from floor to fixture bottom')
      },
      caption: mainScene === 'entryway'
        ? 'Use the extra ceiling height while keeping the front door, landing, stairs, and walking path clear.'
        : mainScene === 'hallway-ceiling'
          ? 'Repeat low-profile fixtures to create an even path without reducing walking clearance.'
          : 'Fixture width, maximum hanging length, and floor clearance are separate measurements.'
    },
    disclaimer: BASE_DISCLAIMER
  }, collectionLinks);
}

function hangingRange(unit) {
  return unit === 'metric' ? '76-91 cm' : '30-36 in';
}

function linearLayout(surfaceLength, surfaceWidth, unit, surfaceName) {
  return {
    id: 'linear',
    label: 'Linear',
    quantity: '1 linear fixture',
    fixtureType: 'One linear chandelier or pendant',
    feasible: true,
    size: `${range(surfaceLength * 0.5, surfaceLength * 0.67, unit)} long; no more than ${single(surfaceWidth * 0.67, unit)} wide`,
    tracking: {
      fixtureTypes: ['chandelier', 'pendant'],
      sizeKind: 'linear',
      sizeMinInches: whole(surfaceLength * 0.5),
      sizeMaxInches: whole(surfaceLength * 0.67),
      sizeMaxWidthInches: whole(surfaceWidth * 0.67),
      quantityMin: 1,
      quantityMax: 1
    },
    spacing: `Center on the ${surfaceName}, with balanced clearance at both ends`,
    measurements: { span: `50-67% of ${surfaceName} length` }
  };
}

function pendantLayout(count, surfaceLength, surfaceWidth, unit) {
  const stockDiameters = [12, 16, 20];
  const widthCap = surfaceWidth * 0.45;
  const lengthCap = (surfaceLength - 12 - (count - 1) * 12) / count;
  const diameter = [...stockDiameters].reverse().find((size) => size <= widthCap && size <= lengthCap);

  if (!diameter) {
    return {
      id: `pendants-${count}`,
      label: `${count} Pendants`,
      quantity: `${count} pendants`,
      fixtureType: `${count} individual pendants`,
      feasible: false,
      reason: 'Not recommended for this surface'
    };
  }

  const internalGap = Math.max(12, Math.floor((surfaceLength - 12 - count * diameter) / (count - 1)));
  const edge = Math.max(6, (surfaceLength - count * diameter - internalGap * (count - 1)) / 2);
  const centerSpacing = diameter + internalGap;

  return {
    id: `pendants-${count}`,
    label: `${count} Pendants`,
    quantity: `${count} pendants`,
    fixtureType: count === 2 ? 'Two individual pendants' : 'Three individual pendants',
    feasible: true,
    size: `${single(diameter, unit)} diameter per shade`,
    tracking: {
      fixtureTypes: ['pendant'],
      sizeKind: 'diameter',
      sizeMinInches: diameter,
      sizeMaxInches: diameter,
      quantityMin: count,
      quantityMax: count
    },
    spacing: `About ${single(internalGap, unit)} between the widest edges of the shades; ${single(centerSpacing, unit)} center-to-center spacing; at least ${single(edge, unit)} of clearance from each end`,
    measurements: {
      shade: `${single(diameter, unit)} diameter each`,
      spacing: `${single(internalGap, unit)} between shade edges`,
      centers: `${single(centerSpacing, unit)} center-to-center`
    }
  };
}

function selectedLayout(input, layoutOptions, primaryLayoutId) {
  const requested = layoutOptions.find((option) => option.id === input.layoutId && option.feasible);
  return requested || layoutOptions.find((option) => option.id === primaryLayoutId && option.feasible) || layoutOptions.find((option) => option.feasible);
}

function islandRecommendation(input) {
  const length = input.lengthInches;
  const width = input.widthInches;
  const layoutOptions = [
    linearLayout(length, width, input.unit, 'island'),
    pendantLayout(2, length, width, input.unit),
    pendantLayout(3, length, width, input.unit)
  ];
  const primaryLayoutId = length <= 48 ? 'linear' : length <= 78 ? 'pendants-2' : 'pendants-3';
  const activeLayout = selectedLayout(input, layoutOptions, primaryLayoutId);
  const standardHeight = `${hangingRange(input.unit)} above the counter`;
  const bottomMin = input.ceilingHeightInches - 72;
  const bottomMax = input.ceilingHeightInches - 66;
  const ceilingDrop = `${range(Math.max(0, bottomMin), Math.max(0, bottomMax), input.unit)} of ceiling-to-fixture hanging length`;

  return addLinks({
    fixtureType: activeLayout.fixtureType,
    fixtureSize: activeLayout.size,
    hangingHeight: standardHeight,
    spacing: activeLayout.spacing,
    layoutOptions,
    activeLayoutId: activeLayout.id,
    tracking: activeLayout.tracking,
    colorTemperature: '3000-3500K · Warm to neutral white',
    explanation: `Compare the linear, two-pendant, and three-pendant layouts. The pendant options use common ${input.unit === 'metric' ? '30, 40, or 50 cm' : '12, 16, or 20 in'} sizes while keeping at least ${single(6, input.unit)} of clearance at each end and enough space between the widest parts of the shades. With a standard counter height, the ceiling height you entered allows about ${ceilingDrop}; confirm the actual counter height and the product's available cord or rod length.`,
    displayRows: [
      displayRow('quantity', 'Quantity', activeLayout.quantity),
      displayRow('size', activeLayout.id === 'linear' ? 'Fixture length' : 'Shade size', activeLayout.size),
      displayRow('height', 'Hanging height', standardHeight),
      displayRow('spacing', 'Spacing', activeLayout.spacing)
    ],
    diagram: {
      mode: 'surface-layout',
      scene: 'island',
      variant: activeLayout.id,
      measurements: { ...activeLayout.measurements, height: standardHeight },
      caption: 'Measure the hanging height from the countertop to the bottom of the fixture. Measure pendant spacing between the widest edges of the shades.'
    },
    disclaimer: BASE_DISCLAIMER
  }, [link('Shop linear chandeliers', COLLECTIONS.chandelier), link('Shop pendant lights', COLLECTIONS.pendant)]);
}

function tableRecommendation(input) {
  const roundTable = input.tableShape === 'round';
  const standardHeight = `${hangingRange(input.unit)} above the tabletop`;
  const manufacturerException = input.unit === 'metric'
    ? 'Use 66-76 cm only if the product installation instructions specifically require this lower range'
    : 'Use 26-30 in only if the product installation instructions specifically require this lower range';

  if (roundTable) {
    const fixtureSize = `${range(input.lengthInches * 0.5, input.lengthInches * 0.67, input.unit)} diameter`;
    return addLinks({
      fixtureType: 'One round chandelier or pendant',
      fixtureSize,
      tracking: {
        fixtureTypes: ['chandelier', 'pendant'],
        sizeKind: 'diameter',
        sizeMinInches: whole(input.lengthInches * 0.5),
        sizeMaxInches: whole(input.lengthInches * 0.67),
        quantityMin: 1,
        quantityMax: 1
      },
      hangingHeight: standardHeight,
      spacing: 'Center on the table, even when the table is not centered in the room',
      installation: { standardHeight, manufacturerException },
      colorTemperature: '2700-3000K · Warm white',
      explanation: 'For a round table, use the tabletop diameter as your reference. For a square table, use the tabletop width. Start with a round fixture about 50-67% of that dimension. Use the lower hanging range only when the product installation instructions require it and sightlines remain clear.',
      displayRows: [
        displayRow('quantity', 'Quantity', '1 round fixture'),
        displayRow('size', 'Fixture diameter', fixtureSize),
        displayGroup('height', 'Hanging height', [
          { label: 'Standard', value: standardHeight },
          { label: 'Product-specific', value: manufacturerException }
        ]),
        displayRow('placement', 'Centering', 'Center on the table, even when it is not centered in the room')
      ],
      diagram: {
        mode: 'table',
        scene: 'dining-table',
        variant: 'round',
        measurements: {
          span: '50-67% of table diameter or width',
          height: standardHeight,
          manufacturerException
        },
        caption: 'Measure the hanging height from the tabletop to the bottom of the fixture, and center the fixture over the table.'
      },
      disclaimer: BASE_DISCLAIMER
    }, [link('Shop chandeliers', COLLECTIONS.chandelier), link('Shop pendant lights', COLLECTIONS.pendant)]);
  }

  const layoutOptions = [
    linearLayout(input.lengthInches, input.widthInches, input.unit, 'table'),
    pendantLayout(2, input.lengthInches, input.widthInches, input.unit),
    pendantLayout(3, input.lengthInches, input.widthInches, input.unit)
  ];
  const activeLayout = selectedLayout(input, layoutOptions, 'linear');
  const explanation = 'For a rectangular table, start with a linear fixture about 50-67% of the table length. For two or three pendants, leave the same amount of space at each end of the table and measure the distance between the widest parts of the shades. Use the lower hanging range only when the product installation instructions require it and sightlines remain clear.';

  return addLinks({
    fixtureType: activeLayout.fixtureType,
    fixtureSize: activeLayout.size,
    hangingHeight: standardHeight,
    spacing: activeLayout.spacing,
    layoutOptions,
    activeLayoutId: activeLayout.id,
    tracking: activeLayout.tracking,
    installation: { standardHeight, manufacturerException },
    colorTemperature: '2700-3000K · Warm white',
    explanation,
    displayRows: [
      displayRow('quantity', 'Quantity', activeLayout.quantity),
      displayRow('size', activeLayout.id === 'linear' ? 'Fixture length' : 'Shade size', activeLayout.size),
      displayGroup('height', 'Hanging height', [
        { label: 'Standard', value: standardHeight },
        { label: 'Product-specific', value: manufacturerException }
      ]),
      displayRow('placement', activeLayout.id === 'linear' ? 'Centering' : 'Spacing', activeLayout.spacing)
    ],
    diagram: {
      mode: 'surface-layout',
      scene: 'dining-table',
      variant: activeLayout.id,
      measurements: {
        ...activeLayout.measurements,
        height: standardHeight,
        manufacturerException
      },
      caption: 'Measure the hanging height from the tabletop to the bottom of the fixture. Size and center the layout from the table, not from the center of the room.'
    },
    disclaimer: BASE_DISCLAIMER
  }, [link('Shop chandeliers', COLLECTIONS.chandelier), link('Shop pendant lights', COLLECTIONS.pendant)]);
}

function vanityRecommendation(input) {
  const mirrorWidth = input.lengthInches;
  let fixtureSize;
  let sizeRange;
  if (mirrorWidth < 30) {
    fixtureSize = input.unit === 'metric' ? '10-13 cm wide and 20-36 cm high each' : '4-5 in wide and 8-14 in high each';
    sizeRange = { widthMinInches: 4, widthMaxInches: 5, heightMinInches: 8, heightMaxInches: 14 };
  } else if (mirrorWidth <= 60) {
    fixtureSize = input.unit === 'metric' ? '13-18 cm wide and 25-46 cm high each' : '5-7 in wide and 10-18 in high each';
    sizeRange = { widthMinInches: 5, widthMaxInches: 7, heightMinInches: 10, heightMaxInches: 18 };
  } else {
    fixtureSize = input.unit === 'metric' ? '15-20 cm wide and 30-51 cm high each' : '6-8 in wide and 12-20 in high each';
    sizeRange = { widthMinInches: 6, widthMaxInches: 8, heightMinInches: 12, heightMaxInches: 20 };
  }

  return addLinks({
    fixtureType: 'A pair of vanity sconces',
    fixtureSize,
    tracking: {
      fixtureTypes: ['wall'],
      sizeKind: 'dimensions',
      ...sizeRange,
      quantityMin: 2,
      quantityMax: 2
    },
    hangingHeight: input.unit === 'metric' ? 'Center of light source about 152-183 cm above the floor' : 'Center of light source about 60-72 in above the floor',
    spacing: 'Place on both sides of the mirror; use an above-mirror bar only when side space is limited',
    colorTemperature: '3000-3500K · Warm to neutral white',
    explanation: `Your mirror is about ${single(mirrorWidth, input.unit)} wide. Side lighting reduces facial shadows. Adjust the mounting height for the primary users and the mirror height. An above-mirror light should not be wider than the mirror.`,
    displayRows: [
      displayRow('quantity', 'Quantity', '2 sconces'),
      displayRow('size', 'Sconce size', fixtureSize),
      displayRow('height', 'Mounting height', input.unit === 'metric' ? 'Center of light source about 152-183 cm above the floor' : 'Center of light source about 60-72 in above the floor'),
      displayRow('placement', 'Position', 'Place one sconce on each side of the mirror')
    ],
    diagram: {
      mode: 'vanity',
      primary: input.unit === 'metric' ? '152-183 cm' : '60-72 in',
      secondary: 'Both sides of mirror',
      scene: 'vanity',
      variant: 'side-sconces',
      measurements: { height: input.unit === 'metric' ? '152-183 cm from floor to center of light source' : '60-72 in from floor to center of light source', position: 'Equal spacing from mirror edges' },
      caption: 'Keep the mirror centered between the sconces and adjust final height for the primary users.'
    },
    disclaimer: `${BASE_DISCLAIMER} Confirm that the fixture is listed for the exact damp or wet location.`
  }, [link('Shop wall lights', COLLECTIONS.wall)]);
}

function bedsideRecommendation(input) {
  const nightstandWidth = input.widthInches;
  const nightstandHeight = input.surfaceHeightInches;
  const shadeMin = Math.max(8, Math.min(18, whole(nightstandWidth * 0.55)));
  const shadeMax = Math.max(shadeMin, Math.min(18, whole(nightstandWidth * 0.8)));

  return addLinks({
    fixtureType: 'Table lamp or bedside wall sconce',
    fixtureSize: `${range(shadeMin, shadeMax, input.unit)} shade diameter`,
    tracking: {
      fixtureTypes: ['table', 'wall'],
      sizeKind: 'diameter',
      sizeMinInches: shadeMin,
      sizeMaxInches: shadeMax,
      quantityMin: 1,
      quantityMax: 2
    },
    hangingHeight: 'Place the lower shade edge near seated eye level',
    spacing: 'Keep the switch reachable from the bed and keep the bulb out of view',
    colorTemperature: '2700K · Warm white',
    explanation: `A nightstand that is ${single(nightstandWidth, input.unit)} wide and ${single(nightstandHeight, input.unit)} high supports this shade range. Use seated eye level as the final check.`,
    displayRows: [
      displayRow('quantity', 'Quantity', '1 fixture on each side you want to light'),
      displayRow('size', 'Shade size', `${range(shadeMin, shadeMax, input.unit)} shade diameter`),
      displayRow('height', 'Visual height', 'Place the lower shade edge near seated eye level'),
      displayRow('placement', 'Reach', 'Keep the switch reachable from the bed and the bulb out of view')
    ],
    diagram: {
      mode: 'bedside',
      primary: 'Seated eye level',
      secondary: 'Shade hides the bulb',
      scene: 'bedside',
      variant: 'table-lamp',
      measurements: { shade: range(shadeMin, shadeMax, input.unit), eyeLevel: 'Lower shade edge near seated eye level' },
      caption: 'View the complete bed and nightstand together, then check the lamp from a seated reading position.'
    },
    disclaimer: BASE_DISCLAIMER
  }, [link('Shop table lamps', COLLECTIONS.table), link('Shop wall lights', COLLECTIONS.wall)]);
}

function sconcePathRecommendation(input) {
  const lengthFeet = input.lengthInches / 12;
  const minimumCount = Math.max(1, Math.ceil(lengthFeet / 8));
  const maximumCount = Math.max(minimumCount, Math.ceil(lengthFeet / 6));
  const countLabel = minimumCount === maximumCount ? `${minimumCount}` : `${minimumCount}-${maximumCount}`;

  return addLinks({
    fixtureType: `${countLabel} wall sconces as a starting point`,
    fixtureSize: 'Choose a diffused sconce that fits the wall and does not extend too far into the walkway',
    tracking: {
      fixtureTypes: ['wall'],
      sizeKind: 'none',
      quantityMin: minimumCount,
      quantityMax: maximumCount
    },
    hangingHeight: input.unit === 'metric' ? 'Center of light source about 152-183 cm above the floor' : 'Center of light source about 60-72 in above the floor',
    spacing: input.unit === 'metric' ? 'Start about 1.8-2.4 m apart' : 'Start about 6-8 ft apart',
    colorTemperature: '2700-3000K · Warm white',
    explanation: 'This estimate is based on the hallway length you entered. Confirm the first and last sconce positions, door swings, light spread, glare, and wall features on site.',
    displayRows: [
      displayRow('quantity', 'Quantity', `${countLabel} wall sconces as a starting point`),
      displayRow('height', 'Mounting height', input.unit === 'metric' ? 'Center of light source about 152-183 cm above the floor' : 'Center of light source about 60-72 in above the floor'),
      displayRow('spacing', 'Spacing', input.unit === 'metric' ? 'Start about 1.8-2.4 m apart' : 'Start about 6-8 ft apart')
    ],
    diagram: {
      mode: 'context',
      primary: `${countLabel} sconces`,
      secondary: input.unit === 'metric' ? '1.8-2.4 m apart' : '6-8 ft apart',
      scene: 'hallway-sconces',
      variant: 'wall-path',
      measurements: {
        height: input.unit === 'metric' ? '152-183 cm from floor to center of light source' : '60-72 in from floor to center of light source',
        spacing: input.unit === 'metric' ? '1.8-2.4 m center-to-center' : '6-8 ft center-to-center'
      },
      caption: 'Use the hallway layout and door locations to position the sconces without creating glare or blocking doors.'
    },
    disclaimer: BASE_DISCLAIMER
  }, [link('Shop wall lights', COLLECTIONS.wall)]);
}

function contextualRecommendation(input) {
  const mappings = {
    sideboard: ['Accent lamps or sconces', '2 lamps or 2 sconces as a balanced starting point', '2700-3000K · Warm white', [link('Shop table lamps', COLLECTIONS.table), link('Shop wall lights', COLLECTIONS.wall)]],
    'task-zone': ['Focused pendant or task light', '1 pendant or 1 task lamp for the selected work area', '3000-3500K · Warm to neutral white', [link('Shop pendant lights', COLLECTIONS.pendant), link('Shop table lamps', COLLECTIONS.table)]],
    'reading-area': ['Floor or table lamp', '1 floor or table lamp for each reading position', '2700-3000K · Warm white', [link('Shop floor lamps', COLLECTIONS.floor), link('Shop table lamps', COLLECTIONS.table)]],
    'accent-wall': ['Wall sconces', '2 sconces as a balanced starting point', '2700-3000K · Warm white', [link('Shop wall lights', COLLECTIONS.wall)]],
    dresser: ['Table lamp or wall sconce', '1-2 lamps or sconces, based on furniture width', '2700K · Warm white', [link('Shop table lamps', COLLECTIONS.table), link('Shop wall lights', COLLECTIONS.wall)]],
    'wet-zone': ['Ceiling light rated for the location', '1 fixture rated for the selected shower area', '3000-3500K · Warm to neutral white', [link('Shop ceiling lights', COLLECTIONS.ceiling)]],
    desk: ['Adjustable desk lamp plus general lighting', '1 adjustable task lamp for each desk position', '3000-3500K · Warm to neutral white', [link('Shop table lamps', COLLECTIONS.table)]]
  };
  const [fixtureType, quantity, colorTemperature, collectionLinks] = mappings[input.target] || mappings['reading-area'];
  const trackingMappings = {
    sideboard: { fixtureTypes: ['table', 'wall'], quantityMin: 2, quantityMax: 2 },
    'task-zone': { fixtureTypes: ['pendant', 'table'], quantityMin: 1, quantityMax: 1 },
    'reading-area': { fixtureTypes: ['floor', 'table'], quantityMin: 1, quantityMax: 1 },
    'accent-wall': { fixtureTypes: ['wall'], quantityMin: 2, quantityMax: 2 },
    dresser: { fixtureTypes: ['table', 'wall'], quantityMin: 1, quantityMax: 2 },
    'wet-zone': { fixtureTypes: ['ceiling'], quantityMin: 1, quantityMax: 1 },
    desk: { fixtureTypes: ['table'], quantityMin: 1, quantityMax: 1 }
  };
  const tracking = trackingMappings[input.target] || trackingMappings['reading-area'];
  const scene = {
    sideboard: 'sideboard-dresser',
    dresser: 'sideboard-dresser',
    'task-zone': 'sink-task',
    'reading-area': 'reading-area',
    'accent-wall': 'accent-wall',
    'wet-zone': 'wet-zone',
    desk: 'desk-task'
  }[input.target] || 'reading-area';
  const placement = {
    sideboard: 'Align the light with the furniture and artwork',
    dresser: 'Align the light with the furniture and nearby reading position',
    'task-zone': 'Center the light over the sink or work area without blocking sightlines',
    'reading-area': 'Place the light beside the reader without exposing the bulb',
    'accent-wall': 'Balance the sconces around artwork and furniture',
    'wet-zone': 'Confirm the exact shower and water-spray area',
    desk: 'Position the task light to avoid shadows from your writing hand and reflections on the screen'
  }[input.target] || 'Confirm the intended furniture position and key sightlines';
  const beforeInstallation = input.target === 'wet-zone'
    ? 'Confirm the wet-location rating and local electrical code requirements'
    : 'Mock up the position before drilling or wiring';

  return addLinks({
    fixtureType,
    fixtureSize: 'General size guidance only; confirm the selected product dimensions',
    tracking: { ...tracking, sizeKind: 'none' },
    hangingHeight: 'Confirm the height with furniture in place and from key sightlines',
    spacing: 'Mock up the position before drilling or wiring',
    colorTemperature,
    explanation: 'No single measurement formula works for every space. Choose a size that fits the furniture or wall feature, the light spread, and the key sightlines.',
    displayRows: [
      displayRow('quantity', 'Quantity', quantity),
      displayRow('type', 'Fixture type', fixtureType),
      displayRow('placement', 'Placement check', placement),
      displayRow('before-installation', 'Before installation', beforeInstallation)
    ],
    diagram: {
      mode: 'context',
      primary: 'Mock up first',
      secondary: 'Confirm product dimensions',
      scene,
      variant: input.target,
      measurements: {},
      caption: `${placement}. ${beforeInstallation}.`
    },
    disclaimer: input.target === 'wet-zone'
      ? `${BASE_DISCLAIMER} Use only fixtures specifically listed for the exact wet location.`
      : BASE_DISCLAIMER
  }, collectionLinks);
}

function recommendLighting(input) {
  const validation = validatePlannerInput(input);
  if (!validation.valid) throw new RangeError(Object.values(validation.errors)[0]);

  const normalized = normalizedInput(input);
  if (normalized.target === 'island') return islandRecommendation(normalized);
  if (normalized.target === 'table') return tableRecommendation(normalized);
  if (normalized.target === 'vanity') return vanityRecommendation(normalized);
  if (normalized.target === 'bedside') return bedsideRecommendation(normalized);
  if (normalized.target === 'sconce-path') return sconcePathRecommendation(normalized);
  if (['open-floor', 'single-height', 'double-height', 'ceiling-path'].includes(normalized.target)) return openFloorRecommendation(normalized);
  return contextualRecommendation(normalized);
}

const GUIDE_VERSION = '2026-08-06';

const GUIDE_EVENT_NAMES = {
  start: 'lighting_guide_start',
  selection: 'lighting_guide_selection',
  input: 'lighting_guide_input',
  recommendation: 'lighting_guide_recommendation',
  shopClick: 'lighting_guide_shop_click'
};

const EVENT_PARAMETER_KEYS = {
  start: [
    'guide_room',
    'guide_target',
    'guide_unit'
  ],
  selection: [
    'guide_room',
    'guide_target',
    'guide_unit',
    'guide_selection_type',
    'guide_selection_value'
  ],
  input: [
    'guide_room',
    'guide_target',
    'guide_unit',
    'guide_input_field',
    'guide_has_input'
  ],
  recommendation: [
    'guide_room',
    'guide_target',
    'guide_unit',
    'guide_layout',
    'guide_fixture_type',
    'guide_size_band',
    'guide_quantity'
  ],
  shopClick: [
    'guide_room',
    'guide_target',
    'guide_layout',
    'guide_fixture_type',
    'guide_size_band',
    'guide_quantity',
    'guide_link_type',
    'guide_destination'
  ]
};

function approvedParameters(eventType, context = {}) {
  const parameters = { guide_version: GUIDE_VERSION };
  EVENT_PARAMETER_KEYS[eventType].forEach((key) => {
    const value = context[key];
    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (!normalized) return;
    parameters[key] = normalized.slice(0, 100);
  });
  return parameters;
}

function deliver(sendEvent, eventName, parameters) {
  if (typeof sendEvent !== 'function') return false;
  try {
    return sendEvent(eventName, parameters) !== false;
  } catch {
    return false;
  }
}

function guideDestinationPath(value, baseUrl = 'https://www.mergelighting.com') {
  try {
    return new URL(value, baseUrl).pathname;
  } catch {
    return '';
  }
}

function createGuideAnalytics(sendEvent) {
  let startDelivered = false;
  let lastRecommendationSignature = '';
  let lastSelectionSignature = '';
  const deliveredInputFields = new Set();

  return {
    start(context) {
      if (startDelivered) return false;
      const parameters = approvedParameters('start', context);
      const sent = deliver(sendEvent, GUIDE_EVENT_NAMES.start, parameters);
      if (sent) startDelivered = true;
      return sent;
    },

    selection(context) {
      const parameters = approvedParameters('selection', context);
      const signature = JSON.stringify(parameters);
      if (signature === lastSelectionSignature) return false;
      const sent = deliver(sendEvent, GUIDE_EVENT_NAMES.selection, parameters);
      if (sent) lastSelectionSignature = signature;
      return sent;
    },

    input(context) {
      const parameters = approvedParameters('input', context);
      const field = parameters.guide_input_field;
      if (!field || deliveredInputFields.has(field)) return false;
      const sent = deliver(sendEvent, GUIDE_EVENT_NAMES.input, parameters);
      if (sent) deliveredInputFields.add(field);
      return sent;
    },

    recommendation(context) {
      const parameters = approvedParameters('recommendation', context);
      const signature = JSON.stringify(parameters);
      if (signature === lastRecommendationSignature) return false;
      const sent = deliver(sendEvent, GUIDE_EVENT_NAMES.recommendation, parameters);
      if (sent) lastRecommendationSignature = signature;
      return sent;
    },

    shopClick(context) {
      const parameters = approvedParameters('shopClick', context);
      return deliver(sendEvent, GUIDE_EVENT_NAMES.shopClick, parameters);
    }
  };
}

window.MergeGuideEngine = { convertMeasurement, getTargetsForRoom, getPlannerConfig, getDefaultPlannerInput, validatePlannerInput, recommendLighting, roomGuideData };

    const analytics = createGuideAnalytics((eventName, parameters) => {
      if (typeof window.gtag !== 'function') return false;
      window.gtag('event', eventName, parameters);
      return true;
    });

    const guideRoot = document.querySelector('#merge-lighting-guide');
    if (!guideRoot) return;

    const ROOM_IMAGES = {
      dining: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/Elowent_Modern_LED_Linear_Alabaster_Pendant_Light_for_Kitchen_Island_5.webp?v=1783932712&width=1400', alt: 'Linear pendant centered above a dining table and chairs', position: '50% 58%' },
      kitchen: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/Retro_Metal_Fragment_Art_Chandelier.webp?v=1785744220&width=1400', alt: 'Sculptural metal linear chandelier above a white kitchen island', position: '50% 62%' },
      living: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/1782885220106.webp?v=1782885799&width=1200', alt: 'Table lamp beside a sofa in a living room seating area', position: '50% 60%' },
      bedroom: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/1784268133112.webp?v=1784268737&width=1200', alt: 'Two pendant lights beside a bed and nightstand', position: '50% 58%' },
      bathroom: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/1782295351084.jpg?v=1782297342&width=1200', alt: 'Two-light vanity fixture centered above a bathroom mirror', position: '50% 56%' },
      entryway: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/2_1b6b7081-caaf-46b0-8457-8b38f3c5a570.webp?v=1779678307&width=1200', alt: 'Wall sconce beside an entry door and console', position: '50% 54%' },
      hallway: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/1783668176394.webp?v=1783668252&width=1200', alt: 'Two ceiling lights repeated along a hallway path', position: '50% 48%' },
      office: { src: 'https://cdn.shopify.com/s/files/1/0951/2907/6033/files/1779763933530.webp?v=1779765430&width=1200', alt: 'Pendant light above a home office desk and chair', position: '50% 60%' }
    };

    const state = { room: 'dining', target: 'table', unit: 'us', tableShape: 'rectangular', layoutId: null };
    let latestRecommendation = null;
    const form = guideRoot.querySelector('#planner-form');
    const analyticsRoot = guideRoot.querySelector('main');
    const roomButtons = [...guideRoot.querySelectorAll('[data-room]')];
    const targetOptions = guideRoot.querySelector('#target-options');
    const unitButtons = [...guideRoot.querySelectorAll('[data-unit]')];
    const tableShapeButtons = [...guideRoot.querySelectorAll('[data-table-shape]')];
    const fields = {
      length: guideRoot.querySelector('#length'),
      width: guideRoot.querySelector('#width'),
      ceilingHeight: guideRoot.querySelector('#ceiling-height'),
      surfaceHeight: guideRoot.querySelector('#surface-height')
    };
    const fieldContainers = {
      length: guideRoot.querySelector('#length-field'),
      width: guideRoot.querySelector('#width-field'),
      ceilingHeight: guideRoot.querySelector('#ceilingHeight-field'),
      surfaceHeight: guideRoot.querySelector('#surface-height-field')
    };
    const unitLabels = {
      length: guideRoot.querySelector('#length-unit'),
      width: guideRoot.querySelector('#width-unit'),
      ceilingHeight: guideRoot.querySelector('#height-unit'),
      surfaceHeight: guideRoot.querySelector('#surface-height-unit')
    };

    function displayNumber(value) {
      return Number(Number(value).toFixed(2));
    }

    function setPressed(buttons, activeButton, attribute = 'aria-pressed') {
      buttons.forEach((button) => button.setAttribute(attribute, String(button === activeButton)));
    }

    function currentConfig() {
      return window.MergeGuideEngine.getPlannerConfig(state.room, state.target, state.tableShape);
    }

    function guideUnit() {
      return state.unit === 'metric' ? 'cm' : 'in';
    }

    function baseGuideContext() {
      return {
        guide_room: state.room,
        guide_target: state.target,
        guide_unit: guideUnit()
      };
    }

    function trackGuideStart() {
      analytics.start(baseGuideContext());
    }

    function trackGuideSelection(type, value) {
      analytics.selection({
        ...baseGuideContext(),
        guide_selection_type: type,
        guide_selection_value: value
      });
    }

    function trackGuideInput(field) {
      analytics.input({
        ...baseGuideContext(),
        guide_input_field: field,
        guide_has_input: true
      });
    }

    function publishShopifyEvent(eventName, eventData) {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish !== 'function') return false;
      try {
        publish.call(window.Shopify.analytics, eventName, eventData)?.catch?.(() => {});
        return true;
      } catch {
        return false;
      }
    }

    function recommendationId() {
      if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
      return `guide-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    function recommendationContext(result) {
      const quantity = result?.displayRows?.find((row) => row.id === 'quantity')?.value;
      return {
        guide_room: state.room,
        guide_target: state.target,
        guide_unit: guideUnit(),
        guide_layout: result?.activeLayoutId || state.layoutId,
        guide_fixture_type: result?.fixtureType,
        guide_size_band: result?.fixtureSize,
        guide_quantity: quantity
      };
    }

    function renderTargets() {
      const targets = window.MergeGuideEngine.getTargetsForRoom(state.room);
      if (!targets.some((target) => target.id === state.target)) state.target = targets[0].id;
      targetOptions.innerHTML = targets.map((target) => `<button class="segment" type="button" data-target="${target.id}" aria-pressed="${target.id === state.target}">${target.label}</button>`).join('');
      [...targetOptions.querySelectorAll('[data-target]')].forEach((button) => {
        button.addEventListener('click', () => {
          trackGuideStart();
          state.target = button.dataset.target;
          trackGuideSelection('target', state.target);
          state.layoutId = null;
          setPressed([...targetOptions.querySelectorAll('[data-target]')], button);
          updatePlannerFields();
          applyDefaults();
          calculate();
        });
      });
    }

    function applyDefaults(preserveTableShape = false) {
      const requestedTableShape = preserveTableShape ? state.tableShape : 'rectangular';
      const defaults = window.MergeGuideEngine.getDefaultPlannerInput(state.room, state.target, state.unit, requestedTableShape);
      if (state.target === 'table') {
        state.tableShape = defaults.tableShape || 'rectangular';
        setPressed(tableShapeButtons, tableShapeButtons.find((button) => button.dataset.tableShape === state.tableShape));
      }
      updatePlannerFields();
      Object.entries(fields).forEach(([name, field]) => {
        field.value = defaults[name] ?? '';
      });
      clearErrors();
    }

    function updatePlannerFields() {
      const config = currentConfig();
      const activeFields = new Set(config.fields);
      Object.entries(fields).forEach(([name, field]) => {
        const active = activeFields.has(name);
        fieldContainers[name].hidden = !active;
        field.disabled = !active;
        if (active) {
          const label = guideRoot.querySelector(`label[for="${field.id}"] span`) || guideRoot.querySelector(`label[for="${field.id}"]`);
          if (label) label.textContent = config.labels[name];
          unitLabels[name].textContent = config.kinds[name] === 'surface'
            ? (state.unit === 'us' ? 'in' : 'cm')
            : (state.unit === 'us' ? 'ft' : 'm');
        }
      });
      guideRoot.querySelector('#table-shape-field').hidden = state.target !== 'table';
      guideRoot.querySelector('#no-measurements-needed').hidden = config.fields.length !== 0;
    }

    function plannerInput() {
      const config = currentConfig();
      const input = {
        room: state.room,
        target: state.target,
        unit: state.unit
      };
      config.fields.forEach((name) => { input[name] = Number(fields[name].value); });
      if (state.target === 'table') input.tableShape = state.tableShape;
      if (state.layoutId) input.layoutId = state.layoutId;
      return input;
    }

    function clearErrors() {
      ['target', 'length', 'width', 'ceilingHeight', 'surfaceHeight'].forEach((name) => {
        const error = guideRoot.querySelector(`#error-${name}`);
        if (error) error.textContent = '';
        if (fields[name]) fields[name].removeAttribute('aria-invalid');
      });
    }

    function showErrors(errors) {
      clearErrors();
      Object.entries(errors).forEach(([name, message]) => {
        const error = guideRoot.querySelector(`#error-${name}`);
        if (error) error.textContent = message;
        if (fields[name]) fields[name].setAttribute('aria-invalid', 'true');
      });
    }

    function renderDiagram(diagram) {
      guideRoot.querySelectorAll('[data-scene]').forEach((scene) => {
        scene.hidden = scene.dataset.scene !== diagram.scene;
      });

      const activeScene = guideRoot.querySelector(`[data-scene="${diagram.scene}"]`);
      if (!activeScene) {
        guideRoot.querySelector('#diagram-caption').textContent = diagram.caption || '';
        return;
      }

      activeScene.dataset.variant = diagram.variant || '';
      activeScene.querySelectorAll('[data-measurement]').forEach((marker) => {
        marker.textContent = '';
        marker.closest('[data-measurement-wrap]')?.setAttribute('hidden', '');
      });
      Object.entries(diagram.measurements || {}).forEach(([key, value]) => {
        const marker = activeScene.querySelector(`[data-measurement="${key}"]`);
        if (!marker || !value) return;
        marker.textContent = value;
        marker.closest('[data-measurement-wrap]')?.removeAttribute('hidden');
      });
      guideRoot.querySelector('#diagram-caption').textContent = diagram.caption || '';
    }

    function resultRowMarkup(row) {
      const value = row.items?.length
        ? `<div class="result-subvalues">${row.items.map((item) => `<span><small>${item.label}</small><strong>${item.value}</strong></span>`).join('')}</div>`
        : `<span>${row.value}</span>`;
      return `<div class="result-row" data-result-row="${row.id}"><dt>${row.label}</dt><dd>${value}</dd></div>`;
    }

    function renderLayoutOptions(result) {
      const container = guideRoot.querySelector('#layout-options');
      const options = result.layoutOptions || [];
      container.hidden = options.length === 0;
      container.innerHTML = options.map((option) => `
        <button type="button" class="layout-option" data-layout-id="${option.id}"
          aria-pressed="${option.id === result.activeLayoutId}"
          ${option.feasible ? '' : 'disabled'}
          title="${option.feasible ? option.quantity : option.reason}">${option.label}</button>
      `).join('');
      container.querySelectorAll('[data-layout-id]').forEach((button) => {
        button.addEventListener('click', () => {
          trackGuideStart();
          state.layoutId = button.dataset.layoutId;
          trackGuideSelection('layout', state.layoutId);
          calculate();
        });
      });
    }

    function buttonLabelMarkup(label) {
      if (label === 'Shop chandeliers') return '<span class="button__label">Shop chandelier<span class="button__terminal-s button__terminal-s--chandeliers">s</span></span>';
      if (label === 'Shop ceiling lights') return '<span class="button__label">Shop ceiling light<span class="button__terminal-s button__terminal-s--ceiling-lights">s</span></span>';
      return label;
    }

    function renderResult(result) {
      guideRoot.querySelector('#result-type').textContent = result.fixtureType;
      renderLayoutOptions(result);
      guideRoot.querySelector('#result-list').innerHTML = result.displayRows.map(resultRowMarkup).join('');
      guideRoot.querySelector('#result-color').textContent = result.colorTemperature;
      guideRoot.querySelector('#result-why').textContent = result.explanation;
      guideRoot.querySelector('#result-disclaimer').textContent = result.disclaimer;
      guideRoot.querySelector('#result-links').innerHTML = result.collectionLinks
        .map(({ label, url }) => `<a class="mlg-button mlg-button--light" href="${url}" aria-label="${label}">${buttonLabelMarkup(label)}</a>`)
        .join('');
      renderDiagram(result.diagram);
      guideRoot.querySelector('#measurement-explanation').textContent = result.explanation;
      const summaryRow = [...result.displayRows].reverse().find((row) => row.value);
      guideRoot.querySelector('#formula-note').textContent = summaryRow ? `${summaryRow.label}: ${summaryRow.value}` : '';
    }

    function calculate(event) {
      if (event) trackGuideStart();
      if (event) event.preventDefault();
      const input = plannerInput();
      const validation = window.MergeGuideEngine.validatePlannerInput(input);
      if (!validation.valid) {
        showErrors(validation.errors);
        const firstInvalid = Object.keys(validation.errors).find((name) => fields[name]);
        if (event && firstInvalid) fields[firstInvalid].focus();
        return;
      }
      clearErrors();
      latestRecommendation = window.MergeGuideEngine.recommendLighting(input);
      renderResult(latestRecommendation);
      if (event) {
        const context = recommendationContext(latestRecommendation);
        analytics.recommendation(context);
        publishShopifyEvent('merge_lighting:guide_recommendation', {
          ...context,
          guide_recommendation_id: recommendationId(),
          guide_tracking: latestRecommendation.tracking
        });
      }
    }

    function setRoom(room) {
      state.room = room;
      state.layoutId = null;
      const roomButton = roomButtons.find((button) => button.dataset.room === room);
      setPressed(roomButtons, roomButton);
      state.target = window.MergeGuideEngine.getTargetsForRoom(room)[0].id;
      renderTargets();
      updatePlannerFields();
      applyDefaults();
      setGuideRoom(room);
      calculate();
    }

    function switchUnit(nextUnit) {
      if (nextUnit === state.unit) return;
      const previousUnit = state.unit;
      const config = currentConfig();
      config.fields.forEach((name) => {
        fields[name].value = displayNumber(window.MergeGuideEngine.convertMeasurement(fields[name].value, config.kinds[name], previousUnit, nextUnit));
      });
      state.unit = nextUnit;
      setPressed(unitButtons, unitButtons.find((button) => button.dataset.unit === nextUnit));
      updatePlannerFields();
      calculate();
    }

    function setGuideRoom(room) {
      const data = window.MergeGuideEngine.roomGuideData[room];
      const image = ROOM_IMAGES[room];
      const activeTab = guideRoot.querySelector(`[data-room-guide="${room}"]`);
      guideRoot.querySelectorAll('[data-room-guide]').forEach((button) => {
        const selected = button === activeTab;
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      guideRoot.querySelector('#room-guide-panel').setAttribute('aria-labelledby', activeTab.id);
      guideRoot.querySelector('#room-guide-label').textContent = data.label;
      guideRoot.querySelector('#room-guide-heading').textContent = data.title;
      guideRoot.querySelector('#room-guide-summary').textContent = data.summary;
      guideRoot.querySelector('#room-guide-context').textContent = data.context;
      guideRoot.querySelector('#room-guide-image').src = image.src;
      guideRoot.querySelector('#room-guide-image').alt = image.alt;
      guideRoot.querySelector('#room-guide-image').style.objectPosition = image.position;
      guideRoot.querySelector('#room-guide-facts').innerHTML = data.facts.map(([label, value]) => `<div class="room-fact"><span>${label}</span><strong>${value}</strong></div>`).join('');
      guideRoot.querySelector('#room-guide-links').innerHTML = data.links.map(({ label, url }) => `<a class="mlg-button mlg-button--ghost" href="${url}" aria-label="${label}">${buttonLabelMarkup(label)}</a>`).join('');
    }

    roomButtons.forEach((button) => button.addEventListener('click', () => {
      trackGuideStart();
      setRoom(button.dataset.room);
      trackGuideSelection('room', button.dataset.room);
    }));
    unitButtons.forEach((button) => button.addEventListener('click', () => {
      trackGuideStart();
      switchUnit(button.dataset.unit);
      trackGuideSelection('unit', button.dataset.unit === 'metric' ? 'cm' : 'in');
    }));
    tableShapeButtons.forEach((button) => button.addEventListener('click', () => {
      trackGuideStart();
      state.tableShape = button.dataset.tableShape;
      trackGuideSelection('table_shape', state.tableShape);
      state.layoutId = null;
      setPressed(tableShapeButtons, button);
      applyDefaults(true);
      calculate();
    }));
    const guideTabs = [...guideRoot.querySelectorAll('[data-room-guide]')];
    guideTabs.forEach((button) => button.addEventListener('click', () => setGuideRoom(button.dataset.roomGuide)));
    guideTabs.forEach((button) => button.addEventListener('keydown', (event) => {
      const navigationKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!navigationKeys.includes(event.key)) return;
      event.preventDefault();
      const currentIndex = guideTabs.indexOf(button);
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? guideTabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + guideTabs.length) % guideTabs.length;
      const nextTab = guideTabs[nextIndex];
      setGuideRoom(nextTab.dataset.roomGuide);
      nextTab.focus();
    }));
    form.addEventListener('submit', calculate);
    form.addEventListener('input', (event) => {
      if (event.target.name) {
        trackGuideStart();
        if (String(event.target.value).trim()) trackGuideInput(event.target.name);
        state.layoutId = null;
        const error = guideRoot.querySelector(`#error-${event.target.name}`);
        if (error) error.textContent = '';
        event.target.removeAttribute('aria-invalid');
      }
    });
    analyticsRoot.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link) return;
      const guide_link_type = link.matches('#result-links a')
        ? 'recommendation'
        : link.matches('#room-guide-links a')
          ? 'room_guide'
          : link.matches('.type-card')
            ? 'category'
            : '';
      if (!guide_link_type) return;
      analytics.shopClick({
        ...recommendationContext(latestRecommendation),
        guide_link_type,
        guide_destination: guideDestinationPath(link.href)
      });
    });
    guideRoot.querySelector('#reset-planner').addEventListener('click', () => {
      state.unit = 'us';
      setPressed(unitButtons, unitButtons.find((button) => button.dataset.unit === 'us'));
      setRoom('dining');
    });

    renderTargets();
    updatePlannerFields();
    applyDefaults();
    setGuideRoom('dining');
    calculate();
})();
